import { css, html, nothing } from 'lit';
import {
    deleteSourceSummary,
    PSD_MIME_TYPE,
    previewOutput,
    separateInput,
    versionGroups,
} from '../../lib/asset-versions.js';
import { BaseComponent } from '../../lib/base-component.js';
import './author-editor.js';

/**
 * An asset's versions by source (issues #199, #217): each version's preview,
 * the input it was made from when that's a separate file (a PSD), who made it
 * (issue #215), and controls to delete a version or a whole source, to make a
 * version its source's current one, to download its input, and to pin it for
 * every app. It asks before deleting, then fires `delete-versions`
 * ({source, version?}), `set-current` ({versionId}), `set-author`
 * ({versionId, body}), `download-input` ({source, version}) or `pin-all`
 * ({source, version}); the page calls the API and passes the reloaded versions
 * back in.
 */
class AssetVersionsTable extends BaseComponent {
    static properties = {
        versions: { type: Array },
        busy: { type: Boolean },
        canPin: { type: Boolean, attribute: 'can-pin' },
        _confirm: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        table { font-size: 0.85rem; margin-bottom: 0; }
        .source-row td { background: #f8f9fa; font-weight: 600; }
        .thumb { width: 40px; height: 40px; object-fit: contain; background: #f8f9fa; border-radius: 4px; }
        .actions { white-space: nowrap; text-align: right; }
        .actions .btn { font-size: 0.75rem; padding: 0.1rem 0.45rem; }
        .file-name { color: #666; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .author { min-width: 9rem; }
    `;

    constructor() {
        super();
        this.versions = [];
        this.busy = false;
        this.canPin = false;
        this._confirm = '';
    }

    _fire(name, detail) {
        this._confirm = '';
        this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
    }

    _confirmButtons(key, label, detail) {
        if (this._confirm !== key) {
            return html`
                <button class="btn btn-outline-danger btn-sm" ?disabled="${this.busy}" @click="${() => (this._confirm = key)}">
                    <i class="bi bi-trash"></i> ${label}
                </button>
            `;
        }
        return html`
            <button class="btn btn-danger btn-sm" ?disabled="${this.busy}" @click="${() => this._fire('delete-versions', detail)}">Confirm</button>
            <button class="btn btn-outline-secondary btn-sm" ?disabled="${this.busy}" @click="${() => (this._confirm = '')}">Cancel</button>
        `;
    }

    /** @param {import('../../lib/asset-versions.js').AssetVersion} v */
    _renderFiles(v) {
        const preview = previewOutput(v);
        const input = separateInput(v);
        const pieces = (v.outputs || []).length;
        return html`
            ${preview?.file.file_name || ''}${preview?.file.width ? html` <span class="text-muted">· ${preview.file.width}×${preview.file.height}</span>` : nothing}
            ${pieces > 1 ? html` <span class="text-muted">· ${pieces} pieces</span>` : nothing}
            ${input ? html` <span class="badge bg-secondary-subtle text-secondary-emphasis ms-1" title="Made from ${input.file.file_name || 'a separate file'}">${input.file.mime_type === PSD_MIME_TYPE ? 'PSD' : 'source'}</span>` : nothing}
        `;
    }

    render() {
        const groups = versionGroups(this.versions || []);
        if (groups.length === 0) return html`<p class="text-muted small mb-0">No versions.</p>`;
        return html`
            <table class="table table-sm align-middle">
                <tbody>
                    ${groups.map(
                        (g) => html`
                        <tr class="source-row">
                            <td colspan="4">${g.source}</td>
                            <td class="actions">
                                ${this._confirm === `source:${g.source}` ? html`<span class="text-danger small me-1">${deleteSourceSummary(groups, g.source)}</span>` : nothing}
                                ${this._confirmButtons(`source:${g.source}`, 'Delete source', { source: g.source })}
                            </td>
                        </tr>
                        ${g.versions.map((v) => {
                            const preview = previewOutput(v);
                            return html`
                            <tr>
                                <td style="width: 52px;">
                                    ${preview ? html`<img class="thumb" src="${preview.file.url}" alt="" loading="lazy">` : nothing}
                                </td>
                                <td style="width: 110px;">
                                    v${v.version}
                                    ${v.is_current ? html`<span class="badge bg-success-subtle text-success-emphasis ms-1">current</span>` : nothing}
                                </td>
                                <td class="file-name" title="${preview?.file.file_name || ''}">${this._renderFiles(v)}</td>
                                <td class="author">
                                    <author-editor .author="${v.author ?? null}" ?busy="${this.busy}"
                                        @author-save="${(e) => this._fire('set-author', { versionId: v.id, body: e.detail.body })}"></author-editor>
                                </td>
                                <td class="actions">
                                    ${
                                        separateInput(v)
                                            ? html`<button class="btn btn-outline-secondary btn-sm me-1" ?disabled="${this.busy}" title="Download the file this version was made from"
                                                @click="${() => this._fire('download-input', { source: v.source, version: v.version })}"><i class="bi bi-download"></i> Input</button>`
                                            : nothing
                                    }
                                    ${
                                        this.canPin && v.source !== 'upload'
                                            ? html`<button class="btn btn-outline-secondary btn-sm me-1" ?disabled="${this.busy}" title="Make every app serve this version"
                                                @click="${() => this._fire('pin-all', { source: v.source, version: v.version })}"><i class="bi bi-pin"></i> Pin for every app</button>`
                                            : nothing
                                    }
                                    ${
                                        !v.is_current
                                            ? html`<button class="btn btn-outline-secondary btn-sm me-1" ?disabled="${this.busy}"
                                                @click="${() => this._fire('set-current', { versionId: v.id })}">Make current</button>`
                                            : nothing
                                    }
                                    ${this._confirmButtons(`version:${g.source}:${v.version}`, 'Delete', { source: g.source, version: v.version })}
                                </td>
                            </tr>
                        `;
                        })}
                    `,
                    )}
                </tbody>
            </table>
        `;
    }
}

customElements.define('asset-versions-table', AssetVersionsTable);
